import {
  cloneElement,
  createElement,
  isValidElement,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

/**
 * `as` renders the observed host element inside the tracker instead of cloning
 * `children`. Server components must use it: in dev, RSC may hand a client
 * component a lazy placeholder instead of an element, and cloneElement on it
 * yields an element of type undefined ("Element type is invalid").
 */
export interface TrackedHostProps {
  as?: 'div' | 'tr';
  className?: string;
  id?: string;
  style?: CSSProperties;
  'data-testid'?: string;
}

export function renderTrackedHost(
  { as, ...hostProps }: TrackedHostProps,
  children: ReactNode,
  ref: Ref<HTMLElement>,
  owner: string,
) {
  if (as) return createElement(as, { ...hostProps, ref }, children);
  if (!isValidElement(children)) {
    throw new Error(`${owner}: pass a single element child, or use \`as\` from server components`);
  }
  return cloneElement(children as ReactElement<{ ref?: Ref<HTMLElement> }>, { ref });
}
